import { Droppable } from '@components/dragAndDrop';
import { Group } from '@components/layout';
import { InternalLink } from '@components/link';
import { Typography } from '@components/typography';
import { SxProps, useTheme } from '@mui/material';
import { DragEvent as ReactDragEvent } from 'react';

export type Breadcrumb = {
  label: string;
  href?: string;
  action?: React.ReactNode;
  onDrop?: (e: ReactDragEvent) => void;
  onDragOver?: (e: ReactDragEvent) => void;
  onDragEnter?: (e: ReactDragEvent) => void;
  onDragLeave?: (e: ReactDragEvent) => void;
  sx?: SxProps;
};

const BreadcrumbItem = ({
  breadcrumb,
  variant,
  isLast = false,
}: {
  breadcrumb: Breadcrumb;
  variant: 'body2' | 'h2' | 'subtitle1';
  isLast?: boolean;
}) => {
  const { palette } = useTheme();
  const content = (
    <Typography variant={variant} fontWeight="400" color={isLast ? 'text.accent' : 'inherit'}>
      {breadcrumb.label}
    </Typography>
  );

  if (breadcrumb.href && !isLast) {
    if (breadcrumb.onDragOver) {
      return (
        <Droppable
          onDragEnter={breadcrumb.onDragEnter}
          onDragLeave={breadcrumb.onDragLeave}
          onDragOver={breadcrumb.onDragOver}
          onDrop={breadcrumb.onDrop}
          dragOverSx={{
            paddingX: 1,
            border: 'none',
            outline: `2px dashed ${palette.primary.main}`,
          }}
        >
          <InternalLink to={breadcrumb.href} unstyled>
            {content}
          </InternalLink>
        </Droppable>
      );
    }
    return (
      <InternalLink to={breadcrumb.href} unstyled>
        {content}
      </InternalLink>
    );
  }

  return <span style={{ display: 'inline-block' }}>{content}</span>;
};

export const Breadcrumbs = ({
  breadcrumbs,
  variant = 'h2',
  sx,
}: {
  breadcrumbs: Breadcrumb[];
  variant?: 'body2' | 'h2' | 'subtitle1';
  sx?: SxProps;
}) => {
  if (!(breadcrumbs?.length > 0)) {
    return null;
  }
  const currentLocation = breadcrumbs[breadcrumbs.length - 1];
  const history = breadcrumbs.slice(0, breadcrumbs.length - 1);
  return (
    <Group gap={0.5} alignItems="center" sx={sx}>
      {history.map((breadcrumb) => (
        <Group key={breadcrumb.label} alignItems="center" gap={0.5}>
          <BreadcrumbItem breadcrumb={breadcrumb} variant={variant} />
          {breadcrumb.action}
          <Typography variant={variant} fontWeight="400" sx={{ margin: '0 12px' }}>
            ›
          </Typography>
        </Group>
      ))}
      <Group alignItems="center" gap={1}>
        <BreadcrumbItem breadcrumb={currentLocation} variant={variant} isLast />
        {currentLocation.action}
      </Group>
    </Group>
  );
};
